import { parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

// Re-export `fromZonedTime` for admin files that need end-of-day / custom
// wall-clock semantics not covered by `pickerDateToTenantInstant`. Keeps
// `apps/web` from having to declare `date-fns-tz` as a direct dependency.
export { fromZonedTime } from 'date-fns-tz';

/**
 * Default fallback timezone used when a tenant has not configured one,
 * when no account context is available (e.g. during the KYC flow before
 * a business_settings row exists), or in pure client-side formatting
 * where no server round-trip has happened yet.
 *
 * Keep this in sync with the DEFAULT clause on
 * `public.business_settings.time_zone` and the fallback inside
 * `public.get_account_timezone()`.
 */
export const DEFAULT_TIMEZONE = 'Asia/Riyadh';

/**
 * Shape of a timezone option returned by the /api/timezones route.
 * Keep in sync with `apps/web/app/api/timezones/route.ts`.
 */
export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

/**
 * Resolve the timezone to use for a given call site.
 *
 * Priority: explicit > tenant > DEFAULT_TIMEZONE.
 *
 * - `explicit` — e.g. a timezone passed directly into a function
 * - `tenant`   — e.g. `business_settings.time_zone` loaded from DB
 *
 * Any empty string, whitespace, or nullish value is ignored.
 */
export function resolveTimezone(
  explicit?: string | null,
  tenant?: string | null,
): string {
  return (
    normalize(explicit) ?? normalize(tenant) ?? DEFAULT_TIMEZONE
  );
}

/**
 * Resolve the effective timezone for a court_locations row.
 *
 * Priority: location.time_zone > tenant.time_zone > DEFAULT_TIMEZONE.
 *
 * Use this when a booking, slot, or display surface has a known location
 * — e.g. the customer picked one of the tenant's locations on /book, or
 * the booking row carries a court_id whose location_id we can resolve.
 */
export function resolveLocationTimezone(
  locationTimeZone?: string | null,
  tenantTimeZone?: string | null,
): string {
  return resolveTimezone(locationTimeZone, tenantTimeZone);
}

function normalize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Short label suitable for inline UI / email / WhatsApp suffixing.
 *   venueTimezoneLabel('Asia/Dubai') -> 'Dubai time'
 *   venueTimezoneLabel('Asia/Dubai', 'ar') -> 'بتوقيت دبي'
 */
export function venueTimezoneLabel(
  tz: string,
  locale: 'en' | 'ar' = 'en',
): string {
  if (tz === 'UTC') return 'UTC';
  const city = (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
  if (locale === 'ar') {
    const ar: Record<string, string> = {
      Riyadh: 'الرياض',
      Dubai: 'دبي',
      Kuwait: 'الكويت',
      Bahrain: 'البحرين',
      Qatar: 'قطر',
    };
    return `بتوقيت ${ar[city] ?? city}`;
  }
  return `${city} time`;
}

/**
 * The browser's IANA timezone, or DEFAULT_TIMEZONE when called server-side
 * or when Intl is unavailable. Intended for client-component use only.
 */
export function getUserTimezone(): string {
  if (typeof Intl === 'undefined') return DEFAULT_TIMEZONE;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Re-format a wall-clock time anchored to one timezone into another.
 * Returns null when the two zones produce the same display string for the
 * given instant (so the caller can skip rendering a redundant "(your time)").
 */
export function convertWallClock(
  isoDate: string, // 'YYYY-MM-DD'
  time: string, // 'HH:MM' or 'HH:MM:SS' (24h)
  fromTz: string,
  toTz: string,
  locale: string = 'en-US',
): string | null {
  if (fromTz === toTz) return null;
  const instant = wallClockToInstant(isoDate, time, fromTz);
  const fromText = formatInstantInTz(instant, fromTz, locale);
  const toText = formatInstantInTz(instant, toTz, locale);
  return fromText === toText ? null : toText;
}

function wallClockToInstant(
  isoDate: string,
  time: string,
  tz: string,
): Date {
  const padded = time.length === 5 ? `${time}:00` : time;
  const naive = new Date(`${isoDate}T${padded}Z`);
  const offset = tzOffsetMinutes(naive, tz);
  return new Date(naive.getTime() - offset * 60_000);
}

function tzOffsetMinutes(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(instant)
    .reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
  const hour = Number(parts.hour) === 24 ? 0 : Number(parts.hour);
  const dateAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second),
  );
  return (dateAsUtc - instant.getTime()) / 60_000;
}

function formatInstantInTz(
  instant: Date,
  tz: string,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(instant);
}

/**
 * Format a timestamp in the tenant's IANA timezone using a date-fns pattern.
 *
 * Use this in admin/tenant UIs where the displayed wall-clock should match the
 * tenant's `business_settings.time_zone` rather than the viewer's browser TZ.
 *
 * Fallback policy when `tz` is invalid (typo, deleted IANA zone, etc.): we
 * retry with `DEFAULT_TIMEZONE` (`'Asia/Riyadh'`), never the viewer's
 * browser TZ. An admin in London viewing a Riyadh tenant must never see
 * London times — defaulting to KSA preserves the tenant-context guarantee
 * even when the saved value is bad.
 *
 * @param value   ISO string, epoch ms, Date, or nullish (empty string returned)
 * @param pattern date-fns format string (e.g. `'PPP p'`, `'yyyy-MM-dd HH:mm'`)
 * @param tz      IANA timezone (e.g. `'Asia/Riyadh'`)
 * @param options optional date-fns options — pass `{ locale }` (e.g. the `ar`
 *                locale) so word-bearing patterns (`MMM`, `EEE`, `a`) render
 *                in the viewer's language instead of English
 * @returns       Formatted output, or `''` for nullish/unparseable input
 */
export function formatInTenantTz(
  value: string | number | Date | null | undefined,
  pattern: string,
  tz: string,
  options?: { locale?: Locale },
): string {
  if (value === null || value === undefined) return '';

  let date: Date;
  try {
    date =
      typeof value === 'string'
        ? parseISO(value)
        : value instanceof Date
          ? value
          : new Date(value);
    if (isNaN(date.getTime())) return '';
  } catch {
    return '';
  }

  try {
    return formatInTimeZone(date, tz, pattern, options);
  } catch {
    // `tz` was bad — fall back to KSA default, never the viewer's browser TZ.
    try {
      return formatInTimeZone(date, DEFAULT_TIMEZONE, pattern, options);
    } catch {
      return '';
    }
  }
}

/**
 * Parse a SQL `date` string (`'YYYY-MM-DD'`) into a local wall-clock Date.
 *
 * Avoids the `new Date('2026-01-15')` pitfall — that produces UTC midnight,
 * which renders as the previous day for any TZ west of UTC. This helper
 * returns local midnight on the given calendar day, no TZ shifting.
 *
 * Returns `null` when input is nullish, not in `'YYYY-MM-DD'` shape, or
 * resolves to an Invalid Date (e.g. `'2026-13-40'`). Callers must guard
 * the result before passing to date-fns `format()` or similar — passing
 * an Invalid Date renders as the literal string `"Invalid Date"` in the UI.
 */
export function parseCalendarDate(
  yyyyMmDd: string | null | undefined,
): Date | null {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const year = y as number;
  const month = m as number;
  const day = d as number;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  // Reject auto-rolled values: `new Date(2026, 12, 15)` silently becomes
  // 2027-01-15 (month overflow). Verify the resulting Date matches what
  // we asked for so callers can rely on `'2026-13-15'` returning `null`.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Convert a picker-selected JS Date to a `'YYYY-MM-DD'` string using its
 * own year/month/day components (no TZ math).
 *
 * Date pickers (e.g. shadcn `<Calendar>`) yield a JS Date at the viewer's
 * browser-local midnight on the day the user clicked. Calling `.toISOString()`
 * shifts that to UTC, producing the previous calendar day for any viewer
 * east of UTC. Use this helper anywhere you'd persist a picker selection
 * back to a SQL `date` column (or any field that represents a calendar day,
 * no time component, no TZ semantics).
 */
export function pickerDateToCalendarString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Convert a picker-selected JS Date to the UTC instant of tenant-midnight
 * on that day. Use when persisting back to a `timestamptz` column so the
 * value lands on the tenant's intended calendar day regardless of which
 * timezone the admin's browser is in.
 *
 * The picker yields a Date whose `getFullYear()/getMonth()/getDate()` reflect
 * the day the user clicked (in browser-local terms). We treat those same
 * Y/M/D values as wall-clock in the tenant's TZ and ask `fromZonedTime`
 * for the equivalent UTC instant.
 *
 * Falls back to `DEFAULT_TIMEZONE` if `tz` is invalid — never the viewer's
 * browser TZ.
 */
export function pickerDateToTenantInstant(date: Date, tz: string): Date {
  // Build a "wall clock" Date that represents the picker's Y/M/D at midnight
  // in *any* zone, then ask fromZonedTime to interpret it as tenant-zone.
  const wallClock = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  // fromZonedTime doesn't throw on unknown IANA zones — it silently returns
  // an Invalid Date. We must check the result instead of relying on try/catch.
  try {
    const result = fromZonedTime(wallClock, tz);
    if (!isNaN(result.getTime())) return result;
  } catch {
    // fall through to default
  }
  return fromZonedTime(wallClock, DEFAULT_TIMEZONE);
}

/**
 * Combine a picker-selected date with a wall-clock time string and emit the
 * UTC instant that represents that wall-clock in the given timezone.
 *
 * Use when a form picks both a date and a time of day separately (e.g. the
 * class booking release picker, or the campaign DateTimeField) and the
 * resulting `timestamptz` must reflect the *tenant* (or user-selected)
 * timezone, not the admin's browser TZ.
 *
 * @param date  JS Date from a picker (Y/M/D components matter; time component
 *              is ignored).
 * @param time  `'HH:MM'`, `'HH:MM:SS'`, or `'H:M'` 24-hour string.
 * @param tz    IANA timezone the wall-clock should be interpreted in.
 *
 * Falls back to `DEFAULT_TIMEZONE` if `tz` is invalid.
 */
export function pickerWallClockToTenantInstant(
  date: Date,
  time: string,
  tz: string,
): Date {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const parts = time.split(':');
  const hh = String(Number(parts[0] ?? 0)).padStart(2, '0');
  const mm = String(Number(parts[1] ?? 0)).padStart(2, '0');
  const ss = String(Number(parts[2] ?? 0)).padStart(2, '0');
  const wallClock = `${y}-${m}-${d}T${hh}:${mm}:${ss}`;

  // fromZonedTime returns an Invalid Date silently for unknown IANA zones —
  // check the result rather than relying on try/catch (which won't fire).
  try {
    const result = fromZonedTime(wallClock, tz);
    if (!isNaN(result.getTime())) return result;
  } catch {
    // fall through to default
  }
  return fromZonedTime(wallClock, DEFAULT_TIMEZONE);
}

/**
 * Calendar-day key (`'YYYY-MM-DD'`) for an instant *as observed in the
 * tenant's TZ*. Two instants share a key iff they fall on the same tenant
 * calendar day — regardless of where the viewer is sitting.
 *
 * Use for day-bucketing UI (e.g. WhatsApp message day separators, "Today" /
 * "Yesterday" labels) where two admins in different timezones must agree
 * on which calendar day a message belongs to.
 *
 * Falls back to `DEFAULT_TIMEZONE` if `tz` is invalid. Returns `''` for
 * nullish/unparseable input.
 */
export function tenantCalendarDayKey(
  value: string | number | Date | null | undefined,
  tz: string,
): string {
  if (value === null || value === undefined) return '';

  let date: Date;
  try {
    date =
      typeof value === 'string'
        ? parseISO(value)
        : value instanceof Date
          ? value
          : new Date(value);
    if (isNaN(date.getTime())) return '';
  } catch {
    return '';
  }

  try {
    return formatInTimeZone(date, tz, 'yyyy-MM-dd');
  } catch {
    try {
      return formatInTimeZone(date, DEFAULT_TIMEZONE, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  }
}
